<template>
    <div>
        <q-dialog
            v-model="showDialog"
            :full-width="false"
            :full-height="false"
            @hide="handleExportPhaseCancelEvent"
        >
            <!-- Download phases file modal starts here -->
            <phase-file-ready-modal
                v-if="downloadablePhaseFileLink"
                :linkedUrl="downloadablePhaseFileLink"
                @closeDialog="closePhaseFileReadyDialog"
            />
            <!-- Download phases file modal ends here -->

            <q-card
                v-else
                class="dialog"
                :class="$q.screen.gt.lg ? 'dialog__lg' : 'dialog__xs'"
            >
                <div class="q-pa-lg">
                    <!-- Export phases modal starts here -->
                    <banner
                        :isVisible="message !== null"
                        :message="message"
                        :isDense="true"
                        :type="currentMessageLevel"
                    />

                    <div
                        v-if="showExportPhasesForm"
                        class="q-mb-md page-description"
                    >
                        <div class="row items-center justify-between">
                            <div class="text-h5 text-weight-medium text-black">
                                {{ headerText }}
                            </div>
                            <q-btn
                                @click="$emit('close')"
                                dense
                                flat
                                icon="close"
                                class="text-black"
                            >
                                <q-tooltip>{{ $t('Close') }}</q-tooltip>
                            </q-btn>
                        </div>
                        {{ $t('Download phases from a specific facility') }}
                    </div>
                    <div>
                        <form
                            @submit.prevent="onSubmit"
                            style="justify-content: center"
                        >
                            <div v-if="showExportPhasesForm">
                                <div class="col-12" v-if="selectFacility">
                                    <q-select
                                        id="facility-select"
                                        v-model="selectedFacility"
                                        :options="getFacilities"
                                        :label="$t('Facility')"
                                        :clearable="true"
                                        :outlined="true"
                                        :disable="isLoading"
                                        bg-color="white"
                                        color="accent"
                                        :style="`padding-bottom: ${$q.screen.lt.md ? '0px' : '20px'}`"
                                    />
                                </div>
                                <div class="col-12">
                                    <date-time-picker
                                        v-model="dateTimePicker.start"
                                        :label="$t('Start')"
                                        class="q-mb-xs"
                                        :class="{
                                            'date-time-picker': $q.screen.lt.md
                                        }"
                                        :rules="[
                                            start =>
                                                validateTimePicker(
                                                    start,
                                                    dateTimePicker.end
                                                )
                                        ]"
                                    />
                                </div>
                                <div class="col-12">
                                    <date-time-picker
                                        v-model="dateTimePicker.end"
                                        :label="$t('End')"
                                        class="q-mb-xs"
                                        :class="{
                                            'date-time-picker': $q.screen.lt.md
                                        }"
                                        :rules="[
                                            end =>
                                                validateTimePicker(
                                                    dateTimePicker.start,
                                                    end
                                                )
                                        ]"
                                    />
                                </div>
                                <div
                                    class="col-12"
                                    :class="
                                        $q.screen.lt.md ? 'q-mb-sm' : 'q-mb-md'
                                    "
                                    v-show="displayAssetOptions"
                                >
                                    <q-radio
                                        v-model="selectedAssetOption"
                                        val="allAssets"
                                        class="q-mr-xl"
                                        :label="$t('All assets')"
                                        :style="
                                            $q.screen.lt.sm
                                                ? ''
                                                : 'padding-right: 5rem'
                                        "
                                        @input="emptySelectedAsset"
                                        color="accent"
                                    />
                                    <q-radio
                                        id="particular-asset-radio"
                                        v-model="selectedAssetOption"
                                        val="particularAsset"
                                        class="q-mr-xl"
                                        :label="$t('Specific assets')"
                                        color="accent"
                                        :disable="!isFacilitySelected"
                                    >
                                        <q-tooltip
                                            :offset="[0, 0]"
                                            v-if="!isFacilitySelected"
                                        >
                                            {{
                                                $t(
                                                    'Please select a facility to show the list of assets.'
                                                )
                                            }}
                                        </q-tooltip>
                                    </q-radio>
                                </div>
                                <!-- asset selection drop down starts here -->
                                <div
                                    class="col-12 q-mb-md"
                                    v-if="
                                        disableSelectInputStyle &&
                                        isFacilitySelected
                                    "
                                >
                                    <remote-select
                                        id="asset-select"
                                        :model-value="selectedAsset"
                                        @update:model-value="
                                            selectedAsset = $event
                                        "
                                        :label="$t('Asset')"
                                        :baseURL="'assets/'"
                                        :urlParams="{
                                            facility: selectedFacility.value,
                                            ordering: 'name'
                                        }"
                                        outlined
                                        searchable
                                        :clearable="clearableAsset"
                                        :readonly="true"
                                        :disable="readOnlyAsset"
                                        :multiple="true"
                                    />
                                    <span
                                        v-if="assetSelectionError"
                                        class="errorMessage"
                                        style="padding-top: 5px"
                                    >
                                        {{ assetSelectionError }}
                                    </span>
                                </div>
                                <!-- asset selection drop down ends here -->
                                <!-- file format selection drop down starts here -->
                                <div class="col-12">
                                    <q-select
                                        v-model="fileFormat"
                                        :label="$t('File Type')"
                                        :options="fileFormatOptions"
                                        style="width: 100%"
                                        outlined
                                        :rules="[validateFileFormat]"
                                        lazy-rules
                                        color="accent"
                                    >
                                    </q-select>
                                </div>

                                <!-- file format selection drop down ends here -->
                                <div class="col-12 q-px-xs q-pb-lg">
                                    <!-- Only downtime and switch events -->
                                    <q-checkbox
                                        v-model="downtimeOnly"
                                        :label="
                                            $t(
                                                'Include downtime and switch events only'
                                            )
                                        "
                                        class="row"
                                        color="accent"
                                    />
                                    <!-- Show production data -->
                                    <span class="row">
                                        <q-checkbox
                                            v-model="withProduction"
                                            :label="
                                                $t(
                                                    'Include product counts and quantities'
                                                )
                                            "
                                            color="accent"
                                        />
                                        <q-icon
                                            class="q-my-md q-mx-sm"
                                            name="far fa-exclamation-circle"
                                            size="xs"
                                            color="grey-7"
                                        >
                                            <q-tooltip :offset="[0, 5]">
                                                {{
                                                    $t(
                                                        'Including production data will increase processing time.'
                                                    )
                                                }}
                                            </q-tooltip>
                                        </q-icon>
                                    </span>
                                </div>
                            </div>
                            <!-- loader will appear on top of the form inputs -->
                            <Loader
                                :show="isLoading"
                                :text="$t('Downloading phases')"
                                :showCancelButton="true"
                                @cancel="handleExportPhaseCancelEvent"
                            />
                            <!-- form action buttons starts here -->
                            <div
                                v-if="downloadablePhaseFileLink === null"
                                class="row justify-end"
                            >
                                <q-btn
                                    id="generate-button"
                                    :label="$t('Generate')"
                                    type="submit"
                                    class="float-right"
                                    color="primary"
                                    :disable="!isFormValid"
                                />
                            </div>
                            <!-- form action buttons ends here -->
                        </form>
                        <!-- Download phases file form ends here -->
                    </div>
                    <!-- Export phases modal ends here -->
                </div>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import Banner from '@components/Banner.vue';
import DateTimePicker from '@components/DateTimePicker.vue';
import Loader from '@components/Loader.vue';
import PhaseFileReadyModal from '@components/PhaseFileReadyModal';
import RemoteSelect from '@components/RemoteSelect.vue';
import AsyncManager from '@utils/asyncManager.js';
import { mapGetters } from 'vuex';

export default {
    name: 'ExportPhases',
    components: {
        Banner,
        PhaseFileReadyModal,
        DateTimePicker,
        RemoteSelect,
        Loader
    },
    emits: ['close'],
    props: {
        facilityId: {
            type: [String, Number],
            required: false,
            default: null
        },
        assetId: {
            type: [String, Number],
            required: false,
            default: null
        },
        enableDowntimeOnly: {
            type: Boolean,
            required: false,
            default: false
        },
        assetSelection: {
            type: String,
            required: false,
            default: 'allAssets'
        },
        headerText: {
            type: String,
            required: false,
            default: 'Export Phases'
        },
        selectFacility: {
            type: Boolean,
            required: false,
            default: false
        },
        startDate: {
            type: String,
            required: false,
            default: null
        },
        endDate: {
            type: String,
            required: false,
            default: null
        }
    },
    data() {
        return {
            MAX_EXPORT_DURATION_DAYS:
                process.env.MAX_EXPORT_DURATION_DAYS || 90,
            downloadablePhaseFileLink: null,
            showExportPhasesForm: true,
            showDialog: true,
            noPhasesMessage: null,
            exportPhasesRequestCancelled: false,
            // either it can be allAssets or particularAsset.
            selectedAssetOption: this.assetSelection,
            // defaulting the selected asset in the asset
            // select component to the props.
            selectedAsset: [],
            downtimeOnly: this.enableDowntimeOnly,
            withProduction: false,
            dateTimePicker: {
                start: '',
                end: ''
            },
            fileFormat: 'XLSX',
            fileFormatOptions: ['XLSX', 'CSV'],
            assetSelectionError: null,
            downloadPhaseFileLink: '',
            checkTaskStatusTimer: null,
            isLoading: false,
            messageLevels: {
                INFO: 'info',
                WARNING: 'warning',
                ERROR: 'error',
                SUCCESS: 'success'
            },
            currentMessageLevel: 'info',
            message: null,
            selectedFacility: null
        };
    },
    created() {
        // initiate the start and end date time picker.
        this.initDateTimePicker();
        this.asyncManager = new AsyncManager({});
        // if assetId is passed as a prop, then we need to
        // add the asset label and id to the selectedAsset array.
        if (this.assetId) {
            this.selectedAsset.push(this.asset);
        }

        // If the facility is passed in as a prop, then set the selected facility to that facility.
        if (this.facilityId) {
            this.selectedFacility = this.getFacilities.filter(
                facility => facility.value === parseInt(this.facilityId)
            )[0];
        }

        // if start and end date is passed as a prop, then add the
        // start and end date to the dateTimePicker object.
        if (this.startDate && this.endDate) {
            this.dateTimePicker.start = this.startDate;
            this.dateTimePicker.end = this.endDate;
        }
    },
    beforeUnmount() {
        this.asyncManager.cancelAsyncLoading({});
    },
    watch: {
        /**
         * If the facility is empty, then set the selected asset option to all assets.
         * @param {Object} facility - Selected facility.
         */
        selectedFacility(facility) {
            if (!facility) {
                this.selectedAssetOption = 'allAssets';
            }
        },
        /**
         * If the asset selection option is changed to all assets,
         * then empty the selected asset array.
         */
        selectedAssetOption() {
            if (this.selectedAssetOption === 'allAssets') {
                this.selectedAsset = [];
            }
        }
    },
    computed: {
        ...mapGetters({
            getAsset: 'facilities/getAsset'
        }),
        /**
         * Show production data.
         * @return {Boolean} - True if we enable production data.
         */
        show_production_data() {
            return this.withProduction;
        },
        /**
         * Get asset by ID.
         * @return {Object}
         */
        asset() {
            const asset = this.getAsset(this.assetId);
            return {
                label: asset.name,
                value: asset.id
            };
        },
        /**
         * Controls the visibility of the asset selection drop down.
         * @returns {Boolean}
         */
        disableSelectInputStyle() {
            if (this.selectedAssetOption === 'allAssets') {
                return false;
            }
            return true;
        },
        /**
         * Controls the visibility of the asset option.
         * @returns {Boolean}
         */
        displayAssetOptions() {
            if (this.assetId !== null) {
                return false;
            }
            return true;
        },
        /**
         * Controls if the asset can be modified.
         * @returns {Boolean}
         */
        readOnlyAsset() {
            if (this.assetId !== null) {
                return true;
            }
            return false;
        },
        /**
         * Controls the clearable option of the asset input.
         * @returns {Boolean}
         */
        clearableAsset() {
            if (this.assetId === null) {
                return true;
            }
            return false;
        },
        /**
         * Get facilities.
         * @return {Object[]} - Array of facilities.
         */
        getFacilities() {
            const facilities = this.$store.getters['facilities/getFacilities'];
            const facilitiesList = [];
            facilities.map(facility =>
                facilitiesList.push({
                    label: facility.name,
                    value: parseInt(facility.id)
                })
            );
            return facilitiesList;
        },
        /**
         * Check if the form is valid.
         * @return {Boolean} - True if the form is valid.
         */
        isFormValid() {
            const isDateRangeValid =
                this.validateTimePicker(
                    this.dateTimePicker.start,
                    this.dateTimePicker.end
                ) === true;
            if (
                this.dateTimePicker.start &&
                this.dateTimePicker.end &&
                this.fileFormat &&
                this.selectedFacility &&
                this.isValidAssetOption &&
                !this.isLoading &&
                isDateRangeValid
            ) {
                return true;
            }
            return false;
        },
        /**
         * Check if the facility is selected.
         * @return {Boolean} - True if the facility is selected.
         */
        isFacilitySelected() {
            if (this.selectedFacility) {
                return true;
            }
            return false;
        },
        /**
         * Validate the selected asset option.
         * @return {Boolean} - True if the selected asset option is valid.
         */
        isValidAssetOption() {
            if (this.selectedAssetOption === 'allAssets') {
                return true;
            }
            if (this.selectedAssetOption === 'particularAsset') {
                if (this.selectedAsset && this.selectedAsset.length > 0) {
                    return true;
                }
            }
            return false;
        }
    },
    methods: {
        /**
         * Emit the close event when user clicks
         * on close button in phases file ready modal
         */
        closePhaseFileReadyDialog() {
            this.$emit('close');
        },
        /**
         * Toggle message banner on modal level
         */
        toggleMessageBanner(message, level = 'info') {
            this.message = message;
            this.currentMessageLevel = level;
        },
        /**
         * Reset the state of the loader
         * and generate button
         */
        resetLoadingState() {
            this.isLoading = false;
            // making sure all the error messages are reset
            this.toggleMessageBanner(null);
        },
        async getPhasesReport(payload) {
            // enable the loading state.
            this.isLoading = true;
            const phasesReportUrl = 'export/phases/';
            try {
                const result = await this.asyncManager.asyncLoad(
                    phasesReportUrl,
                    payload
                );
                this.isLoading = false;
                this.downloadablePhaseFileLink = result[0];
                if (this.downloadablePhaseFileLink === null) {
                    this.toggleMessageBanner(
                        this.$t('No phases found'),
                        'warning'
                    );
                } else {
                    this.showExportPhasesForm = false;
                }
            } catch (e) {
                const error =
                    e?.response?.data && typeof e.response.data === 'object'
                        ? e.response.data
                        : {};
                this.resetLoadingState();
                if (
                    error['detail'] &&
                    error['detail'] ===
                        'You do not have permission to perform this action.'
                ) {
                    this.toggleMessageBanner(
                        this.$t(
                            'You do not have permission to perform this action.'
                        ),
                        'error'
                    );
                } else if (error['end']) {
                    this.toggleMessageBanner(
                        this.$t(
                            'The selected date range is too large. Please choose a shorter duration.'
                        ),
                        'error'
                    );
                } else {
                    this.toggleMessageBanner(
                        this.$t(
                            'There was an error generating reports. Please try again.'
                        ),
                        'error'
                    );
                }
            }
        },
        handleExportPhaseCancelEvent() {
            // since cancel button is also acting as
            // the button to cancel the request.
            // toggling the export phase request
            // cancelled to true here.
            this.exportPhasesRequestCancelled = true;
            // stop async loading
            this.asyncManager.cancelAsyncLoading({});
            // propagate the cancel event to the parent
            // component so that it can toggle the export
            // phases component.
            this.$emit('close');
        },
        /**
         * Initiate the start and end properties
         * of dateTimePicker data property to one
         * week from today to current time respectively.
         */
        initDateTimePicker() {
            // init start Date string at one hr before now
            this.dateTimePicker.start = new Date(
                new Date().getTime() - 1000 * 60 * 60 * 24 * 7
            ).toISOString();
            // init end Date string at now
            this.dateTimePicker.end = new Date().toISOString();
        },
        /**
         * If all assets radio button is
         * re-checked the asset value of
         * remote-select component should
         * be empty.
         */
        emptySelectedAsset() {
            this.selectedAsset = [];
        },
        validateFileFormat() {
            if (!this.fileFormatOptions.includes(this.fileFormat)) {
                return this.$t('The supported file formats are:').concat(
                    this.fileFormatOptions
                );
            } else {
                return true;
            }
        },
        /**
         * Convert UTC date time to timestamp
         */
        getTimeStamp(dateTime) {
            return Math.floor(new Date(dateTime).getTime() / 1000);
        },
        onSubmit() {
            this.toggleMessageBanner(null);

            if (
                this.selectedAssetOption === 'particularAsset' &&
                this.selectedAsset &&
                this.selectedAsset.length === 0
            ) {
                this.assetSelectionError = this.$t('Please select an asset');
            } else {
                // put the generate button in disable state
                // so the user cannot click it while phases
                // file is being generated.
                this.isLoading = true;
                // generate the phases.
                this.generatePhases();
            }
        },
        generatePhases() {
            // Creates an array of asset ids from the selected assets.
            const assetsIds = this.selectedAsset.map(asset => asset.value);

            let payload = {
                start: this.getTimeStamp(this.dateTimePicker.start),
                end: this.getTimeStamp(this.dateTimePicker.end),
                facility: this.selectedFacility.value,
                // If no assets are selected, it means "all assets," so send null, else send the array of asset ids.
                assets: assetsIds.length > 0 ? assetsIds : null,
                downtime_only: this.downtimeOnly,
                file_format: this.fileFormat.toUpperCase(),
                show_production: this.show_production_data,
                cancelable: true
            };

            // remove key if they have null values
            Object.keys(payload).forEach(
                key => payload[key] === null && delete payload[key]
            );

            // generate the phases report
            this.getPhasesReport(payload);
        },
        /**
         * Check if the date time is valid.
         * @param {String} start - Start date time.
         * @param {String} end - End date time.
         * @returns {Boolean|String} - True if the date time is valid.
         */
        validateTimePicker(start, end) {
            const startDate = new Date(start);
            const endDate = new Date(end);
            if (startDate >= endDate) {
                return this.$t('Start time should come before the end time.');
            }
            // limit the date range to 90 days
            const dateRangeLimit =
                this.MAX_EXPORT_DURATION_DAYS * 24 * 60 * 60 * 1000;
            const dateDifference = endDate - startDate;
            if (dateDifference > dateRangeLimit) {
                return this.$t(
                    'The selected date range is too large. Please choose a shorter duration.'
                );
            }
            return true;
        }
    }
};
</script>

<style scoped>
.errorMessage {
    color: red;
    font-size: 0.9rem;
}

:deep(.date-time-picker .q-field__bottom) {
    padding: 4px 12px 0 !important;
}
</style>
